import React from 'react';
import {
  Dialog,
  DialogProps,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { SlideUpTransition } from './SlideTransition';

export interface DetailPanelProps extends Omit<DialogProps, 'fullScreen' | 'TransitionComponent'> {
  /**
   * Whether the panel is open
   */
  open: boolean;
  /**
   * Callback when panel should close
   */
  onClose: () => void;
  /**
   * Maximum width of the dialog on desktop
   * @default 'sm'
   */
  maxWidth?: DialogProps['maxWidth'];
  /**
   * Header content (typically DetailPanelHeader)
   */
  header?: React.ReactNode;
  /**
   * Main content of the panel
   */
  children: React.ReactNode;
  /**
   * Footer content (typically action buttons)
   */
  footer?: React.ReactNode;
  /**
   * Whether to use slide transition on mobile
   * @default true
   */
  useSlideTransition?: boolean;
}

/**
 * Reusable detail panel component that wraps MUI Dialog with
 * consistent mobile/desktop behavior:
 * - Mobile: Full-screen with slide-up transition
 * - Desktop: Centered modal with max width
 *
 * @example
 * <DetailPanel
 *   open={isOpen}
 *   onClose={handleClose}
 *   header={
 *     <DetailPanelHeader
 *       title="Alert Details"
 *       onClose={handleClose}
 *     />
 *   }
 *   footer={
 *     <Box sx={{ p: 2 }}>
 *       <Button onClick={handleAction}>Take Action</Button>
 *     </Box>
 *   }
 * >
 *   <Box sx={{ p: 2 }}>
 *     Panel content here
 *   </Box>
 * </DetailPanel>
 */
export function DetailPanel({
  open,
  onClose,
  maxWidth = 'sm',
  header,
  children,
  footer,
  useSlideTransition = true,
  PaperProps,
  ...props
}: DetailPanelProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      TransitionComponent={isMobile && useSlideTransition ? SlideUpTransition : undefined}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        ...PaperProps,
        sx: {
          ...(isMobile ? {} : { maxHeight: '90vh' }),
          ...PaperProps?.sx,
        },
      }}
      {...props}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {header}

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
          }}
        >
          {children}
        </Box>

        {footer && (
          <Box
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.default',
              // Safe area padding for mobile devices with home indicator
              pb: isMobile ? 'calc(16px + env(safe-area-inset-bottom))' : 0,
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

export default DetailPanel;
